import {
	CommunityLinkData,
	ExperimentModel,
	FaceStatus,
	OSSModel,
	OrderModel,
	ProfileResponse,
	RFResponse,
	RFResponseCommon,
	RegisterAccountResponse,
	TickerResponse,
	UserInfo,
	UserOpenBrokerStatus,
	UserPrivacyInfo,
} from "@/interfaces/common";
import * as http from "@/utils/request";
import rfStore from "@/utils/rfStore";
import { useEffect, useState } from "react";
import useSWR from "swr";
const baseUrl = process.env.NEXT_PUBLIC_BASE_API;

export const getOSSConfig = async () => {
	const url = baseUrl + "/storage/api/storage/token/oss";

	try {
		const { code, message, data } = await http.post<null, RFResponseCommon<OSSModel>>(url);

		if (code !== 200) {
			throw Error(message);
		}

		return data;
	} catch (error) {
		return Promise.reject(error);
	}
};

/**
 * OpenBrokerStatus
 */
export function useOpenBrokerStatus() {
	const uid = rfStore.getRFState()?.userId;
	const url = baseUrl + "/promotion/api/masterAccount/status/v2";

	const { data } = useSWR(
		uid ? url : null,
		async (url) =>
			await http.get<RFResponseCommon<{ masterAccountOpenStatus: UserOpenBrokerStatus }>>(url).then((res) => res.data),
	);

	return {
		openBrokerStatus: data?.masterAccountOpenStatus,
	};
}

/**
 * 获取他人的信息
 */
export function useUserInfo(userId: string) {
	const url = `${baseUrl}/profile/api/users/profile/others?userId=${userId}`;
	const selfUserId = rfStore.getRFState()?.userId;
	const { data, isLoading } = useSWR(
		userId ? url : null,
		async (url) =>
			await http
				.get<RFResponseCommon<UserInfo>>(url, {
					headers: { userId: selfUserId as string },
				})
				.then((res) => res.data),
	);

	return {
		data,
		isLoading,
	};
}

export async function fetchUserInfo(userId: string) {
	const url = `${baseUrl}/profile/api/users/profile/others?userId=${userId}`;
	const selfUserId = rfStore.getRFState()?.userId;
	const data = await http
		.get<RFResponseCommon<UserInfo>>(url, {
			headers: { userId: selfUserId as string },
		})
		.then((res) => res.data);

	return data;
}

/**
 * 获取人脸识别
 */

export function useFaceRecognition() {
	const selfUserId = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/masterAccount/api/judge/face/recognition`;

	const { data, isLoading } = useSWR(
		selfUserId ? url : null,
		async (url) =>
			await http
				.get<RFResponseCommon<FaceStatus>>(url, {
					headers: { userId: selfUserId as string },
				})
				.then((res) => res.data),
	);

	return {
		data,
		isLoading,
	};
}

/**
 * 获取用户昵称等信息
 */
export function useProfile() {
	const uid = rfStore.getRFState()?.userId;

	const url = `${baseUrl}/profile/api/users/profile`;
	const { data, isLoading } = useSWR(
		uid ? url : null,
		async (url) => await http.get<RFResponseCommon<ProfileResponse>>(url).then((res) => res.data),
	);

	return {
		data,
		isLoading,
	};
}

/**
 * 获取用户注册手机号&邮箱
 *
 */
export function useUserRegisterAccount() {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/account/api/users/accounts/${uid}/identities`;

	const { data, isLoading } = useSWR(
		uid ? url : null,
		async (url) => await http.get<RFResponseCommon<RegisterAccountResponse>>(url).then((res) => res.data),
	);

	return {
		data,
		isLoading,
	};
}

/**
 *
 * 获取返佣金模版
 *
 */
export function useRebateTemplate() {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/promotion/api/commissionFactor`;
	const { data, isLoading } = useSWR(
		uid ? url : null,
		async (url) => await http.get<RFResponseCommon<any>>(url).then((res) => res.data),
	);

	return {
		data,
		isLoading,
	};
}

/*
 * 获取社群链接
 */
export function useCommunityLink() {
	const url = `${baseUrl}/configuration/api/platform-links`;

	const { data, isLoading } = useSWR(
		url,
		async (url) => await http.get<RFResponseCommon<CommunityLinkData[]>>(url).then((res) => res.data),
	);

	return {
		data,
		isLoading,
	};
}

/**
 *
 * 查询用户隐私信息
 */
export function useUserPrivacyInfo() {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/_api/users/accounts/security/status`;

	const { data, isLoading } = useSWR(
		uid ? url : null,
		async (url) => await http.get<RFResponseCommon<UserPrivacyInfo>>(url).then((res) => res.data),
	);

	return {
		data,
		isLoading,
	};
}

/*
 * 获取APP 设置
 * version 传当前时间可以获取最新的设置
 */
export function useAppSetting(timestamp?: number) {
	const suffix = timestamp ? `?version=${timestamp}` : "";
	const url = `${baseUrl}/configuration/api/settings${suffix}`;

	const { data, isLoading } = useSWR(
		url,
		async (url) => await http.get<RFResponseCommon<any>>(url).then((res) => res.data),
		{
			revalidateOnFocus: false,
		},
	);

	return {
		data,
		isLoading,
	};
}

export const fetchAppSettings = async (timestamp?: number) => {
	const suffix = timestamp ? `?version=${timestamp}` : "";
	const url = `${baseUrl}/configuration/api/settings${suffix}`;
	return await http.get<RFResponseCommon<{ key: string; value: string }[]>>(url).then((res) => res.data);
};

/**
 * 绑定邀请码
 */
export const bindInviter = async (inviterCode: string) => {
	try {
		const url = `${baseUrl}/promotion/api/invite/bindInviter?inviteCode=${inviterCode}`;
		await http.post<null, RFResponseCommon<null>>(url).then((res) => res.data);

		return true;
	} catch (error) {
		throw error;
	}
};

/**
 * 检查用户时候注册过
 * - type： 1：手机 2：邮箱
 * 手机号填写规则：国际区号+手机号，国际区号四位，不够补0，例：中国86，补0后：0086
 */
export const checkUserRegister = async (type: number, identity: string) => {
	try {
		const url = `${baseUrl}/account/api/users/accounts?type=${type}&identity=${identity}`;
		const { data } = await http.get<RFResponseCommon<{ existent: boolean }>>(url);
		return data.existent;
	} catch (error) {
		throw error;
	}
};

/**
 * 检查结单是否有效
 */
export const checkStatementUrlIsCorrect = async (statementFile: string) => {
	const url = `${baseUrl}/statement/api/userId`;

	try {
		const { code, message, data } = await http.post<{ fileName: string }, RFResponseCommon<number>>(url, {
			fileName: statementFile,
		});

		if (code !== 200) {
			throw Error(message);
		}

		const uid = data.toString();
		const selfUid = rfStore.getRFState()?.userId;

		if (uid !== selfUid) {
			throw Error("无效的结单");
		}
		return true;
	} catch (error) {
		throw error;
	}
};

/**
 * 用户是否在白名单
 * key: 在后台添加配置
 */
export const checkUserIsWhiteList = async (key: string) => {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/configuration/api/users/${uid}/settings`;

	try {
		const { data } = await http.get<RFResponseCommon<{ key: string; value: string; version: number }[]>>(url);

		return data.find((item) => item.key === key)?.value === "true";
	} catch (error) {
		throw error;
	}
};

/**
 * @summary 获取用户分组
 * @param {string} groupId 分组id
 * @returns { data: ExperimentModel | undefined; isLoading: boolean }
 */
export function useExperienceGroup(groupId: string) {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/experiment/api/groups/${groupId}/users/${uid}`;

	const { data, isLoading, error } = useSWR(
		uid ? url : null,
		async (url) => await http.get<RFResponseCommon<ExperimentModel>>(url).then((res) => res.data),
		{
			revalidateOnFocus: false,
		},
	);

	return {
		data,
		isLoading,
	};
}

/**
 * @summary 获取站跌幅
 */
function getChangeProfit(preClose: number, close: number) {
	if (preClose === 0 || close === 0) return 0;
	return ((close - preClose) / preClose) * 100;
}
/**
 * @summary 行情
 */
export const fetchTickerChart = async (symbol: string, market: string) => {
	const url = `${baseUrl}/ticker/api/chart/${market}/${symbol}?span=1month`;
	return await http.get<RFResponse<TickerResponse>>(url).then((res) => res.data);
};

export const useTickerChart = (symbols: { symbol: string; market: string }[], start: boolean) => {
	const [data, setData] = useState<
		{
			symbol: string;
			profit: number;
			chart: TickerResponse;
		}[]
	>([]);
	const [isLoading, setIsLoading] = useState(true);
	useEffect(() => {
		if (start) return;

		const promiseAll = symbols.map(async (item) => {
			const data = await fetchTickerChart(item.symbol, item.market);
			const first = data.historicals[0];
			const last = data.historicals[data.historicals.length - 1];
			if (!first || !last) return { symbol: item.symbol, profit: 0, chart: data };
			const profit = getChangeProfit(first.close, last.close);
			return {
				symbol: item.symbol,
				profit,
				chart: data,
			};
		});
		Promise.all(promiseAll).then((res) => {
			setData(res);
			setIsLoading(false);
		});
	}, [start]);

	return {
		data,
		isLoading,
	};
};

/**
 * @summary 设置用户属性
 */
export async function setUserSettings(payload: { key: string; value: string | boolean }) {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/configuration/api/users/${uid}/settings`;
	return await http.post<{ key: string; value: string | boolean }, RFResponseCommon<null>>(url, {
		key: payload.key,
		value: payload.value,
	});
}

/**
 * @summary 获取用户属性
 */
export function useUserSettings() {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/configuration/api/users/${uid}/settings`;
	const { data, isLoading, mutate } = useSWR(
		uid ? url : null,
		async (url) =>
			await http.get<RFResponseCommon<{ key: string; value: string; version: number }[]>>(url).then((res) => res.data),
	);

	return {
		data,
		isLoading,
		mutate,
	};
}

/**
 * 与 fe-uniflux `CommonApi.getRegionMatch` 路径一致（h5 未依赖 @flow-ai/shared）
 * GET /masterAccount/api/regions/{region}/match
 */
function getRegionMatchUrl(region: string) {
	const encoded = encodeURIComponent(region);
	return `${baseUrl}/masterAccount/api/regions/${encoded}/match`;
}

/**
 * @summary 是否命中指定 region 的业务限制（如 NZ），由服务端综合判断
 */
export function useNzRegionMatch(region: string = "NZ") {
	const uid = rfStore.getRFState()?.userId;
	const url = uid ? getRegionMatchUrl(region) : null;
	const { data, isLoading, mutate } = useSWR(
		url,
		async (u) => await http.get<RFResponse<boolean>>(u).then((res) => res.data),
		{ revalidateOnFocus: false },
	);

	return {
		data,
		isLoading,
		mutate,
	};
}

/**
 * 获取订单详情
 */
export function useOrderDetail(orderId: string) {
	const url = `${baseUrl}/portfolio/api/orders/${orderId}`;
	const { data, isLoading, error } = useSWR(
		orderId ? url : null,
		async (url) => await http.get<RFResponseCommon<OrderModel>>(url).then((res) => res.data),
	);

	return {
		data,
		isLoading,
		error,
	};
}
